import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthenticationService } from './authentication.service';
import { LogCategory, LogCoreService, LoggerService, LogType } from './logger.service';

const CACHE_SIZE = 1;

@Injectable()
export class UtilityService {
    private _lookups$: Observable<any>;
    public static maxDate: Date = new Date('9998-12-31 00:00:00.000+00:00');
    public static maxDateString: string = '9998-12-31T00:00:00';
    public static nullDateString: string = '1900-01-01T00:00:00';
    public static nullDate: Date = new Date(UtilityService.nullDateString);

    // Max file size we can currently upload is 4Mb.  To increase this you also need to update the web.config. See this article: https://stackoverflow.com/questions/3853767/maximum-request-length-exceeded
    public static maxFileSize: number = 4194304;

    constructor(
        private http: HttpClient,
        private logger: LoggerService,
        private auth: AuthenticationService,
        private toaster: ToastrService
    ) { }
    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.CoreService, LogCoreService.Utility, category);
    }

    private flushServerCache(): void {
        const url = environment.apiUrl + '/utility/flushcache';        
        this.log("flushServerCache$");
        this.http
            .get<string>(url)
            .subscribe(() => {
                this.log("flushServerCache$", "Server cache flushed", LogCategory.SubscriptionData);
                this.toaster.info('Refresh is complete. Please reload the page to see your updates.');
            });
    }

    // Calls the diagnostics endpoint, which posts diagnostic data to supplemental data, and returns a response 
    // message supplemental data record ID as a string 
    public diagnose$: Observable<string> = this.http
        .get<string>(environment.apiUrl + '/utility/diagnose')
        .pipe(
            tap(a => {
                this.log("diagnose$");
            })
        ); 

    //  Note:  This is specifically only for filters in the catalog.
    public get filterLookups$(): Observable<any> {
        if (this._lookups$) {
            // this.log("lookups$", 'returned cached catalog lookups$')
        } else {
            this._lookups$ = this.http.get<any>(environment.apiUrl + '/utility/filterlookups')
                .pipe(
                    tap((res) => {
                        this.log('getlookups$', 'fetched catalog lookups via api', LogCategory.SubscriptionData);
                    }),
                    catchError(this.handleError('getLookups', [])),
                    shareReplay(CACHE_SIZE)
                );
        }
        return this._lookups$;
    }

    public lookups$(types: string[]): Observable<any> {
        const queryString = types.map(type => `lookupTypes=${encodeURIComponent(type)}`).join('&');

        return this.http.get<any>(`${environment.apiUrl}/utility/lookups?${queryString}`);
    }

    // Returns either todays date or the overrideDate
    public getCurrentDate(): Date {
        var now = new Date();
        var token = this.auth.token;
        if (token.isFound) {
            var payload = token.payload;
            if (!!payload['Kfbs.Environment.OverrideNowDate']) {
                var nowOverride = localStorage.getItem('nowOverride');
                if (nowOverride != null)
                    now = new Date(nowOverride);
            }
        }
        return now;
    }

    // Returns a deeply nested value from an associate array if it's present, or the default value if it's not. 
    public getNestedValue<T>(obj: any, location: string, defaultValue: T): T {
        var result = location.split(".").reduce(function (prev, curr) {
            return ( (typeof prev == "undefined") || (prev === null)) ? prev : prev[curr];
        }, obj);
        return (typeof result == "undefined") ? defaultValue : result; 
    }

    // Splits a string on a delimiter so that the resulting strings are as long as possible but not longer than a spcified limit.
    // For example, "alex;ed;paula;crystal;sam;simon;john;" split with (';', 20) will return { 'alex;ed;paula', 'crystal;sam;simon', 'john' }
    // Useful, for example, for splitting mailtos. 
    public splitToSize(input: string, delimiter: string, maxLength: number): string[] {

        let retVal = []; 

        const tokens = input.split(delimiter);
        let line = ''; 

        // Build each line so that it's shorter than the maxLength 
        var charCount = 0;
        const delimiterLength = delimiter.length; 
        for (let i = 0; i < tokens.length; i++) {
            var token = tokens[i];
            charCount += token.length + delimiterLength;

            if (charCount > maxLength) {
                retVal.push(line);
                charCount = 0;
                line = '';
            }

            line += token + delimiter;
        }

        // Push the last line
        if (line.length > 0) {
            retVal.push(line);
        }

        return retVal; 
    }

    private handleError<T>(operation = 'operation', result?: T) {
        return (error: any): Observable<T> => {

            // TODO: send the error to remote logging infrastructure
            console.error(error); // log to the console instead

            // Let the app keep running by returning an empty result.
            return of(result as T);
        };
    }

    //  Downloads a file and triggers a toastr to indicate a download occurred
    public downloadFile(fileName: string, url: string) {
        var anchor = document.createElement("a");
        anchor.download = decodeURIComponent(fileName);
        anchor.href = url;
        anchor.click();
        this.toaster.info('File saved to your download folder');
    }
    //#region  Used in Editable Fields
    //  Used for checking file sizes.  Returns file size in MB up to 2 decimal places.
    public static convertBytesToMegabytes(bytes) {
        return Math.round((bytes / 1024 / 1024) * 100) / 100;
    }

    //  Leaving this commented out for now as there is an existing downloadFile already in MyKE but it's shaped different.
    //  This is what the editable fields use.  Once we've implemented a file input test to make sure we know if the existing
    //  one will work okay or if we're going to need to figure out implementation using the below code.
    ////  Automatically download the blob file provided
    //public static downloadFile(blob: Blob, fileName: string) {
    //    var url = window.URL.createObjectURL(blob);

    //    //  FileNames usually come with "attachment; filename=" appended to the front of the name.  This check
    //    //  removes that text if it's still present on the fileName.
    //    var matches = this.filenameRegex.exec(fileName);
    //    if ((matches != null) && matches[1])
    //        fileName = matches[1].replace(/['"]/g, '');

    //    //  Blobs do not have file names.  This allows for an automatic download of the file while
    //    //  retaining the original file name.
    //    var anchor = document.createElement("a");
    //    anchor.download = fileName;
    //    anchor.href = url;
    //    anchor.click();
    //}
    //#endregion

    //#region local storage
    public static saveLocalData(storageKey:string, storageObject:any) {
        if (!!storageKey) {
            var sortedItems = Object.keys(storageObject).sort().reduce((r, k) => (r[k] = storageObject[k], r), {});
            var storageValue = JSON.stringify(sortedItems);

            localStorage.setItem(storageKey, storageValue);
        }
    }

    public static loadLocalData(storageKey:string) {
        return JSON.parse(localStorage.getItem(storageKey));
    }

    public static deleteLocalData(storageKey: string) {
        localStorage.removeItem(storageKey);

    }
    //#endRegion
}

//  todo:  these should go into the shared core once it exists. - Jan 2026 CS
export function toStandardDate(date: Date) {
    return date.toLocaleDateString('en-US', { month: "short", day: "numeric", year: 'numeric' });
}

export function toStandardTime(date: Date) {
    return date.toLocaleTimeString('en-US', { hour: "2-digit", minute: "2-digit" });
}

export function toStandardDateTime(date: Date) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
        ' ' + date.toLocaleTimeString('en-US', { hour: "2-digit", minute: "2-digit" });
}
